import { Dialog } from "@kobalte/core/dialog";
import { AnimateChildren } from "@storytell/ui/animation";
import { createEffect, createSignal, on, onCleanup, onMount } from "solid-js";
import { useWire } from "~/wire/WireProvider";
import { type Filters, serverFiltersToUiFilters, useFilterBuilder } from "../filtering/filterBuilder";
import type { Asset, Concept, ConceptId, ViewId } from "@storytell/types";
import { useEntitySelection } from "./useEntitySelection";
import { subscribeToGlobalEvent } from "@storytell/events";
import { KnowledgePaneContent } from "./KnowledgePaneContent";

export const KnowledgePane = () => {
  const wire = useWire();
  const { knowledgeUi, sync: s } = wire;

  const [search, setSearch] = createSignal("");

  const [filterExpanded, setFilterExpanded] = createSignal(false);

  const assetsSelection = useEntitySelection();
  const conceptsSelection = useEntitySelection();

  const [editView, setEditView] = createSignal(false);

  const [selectedConcept, setSelectedConcept] = createSignal<ConceptId | "">("");

  const hasViewChanges = () => {
    const active = knowledgeUi.activeView();
    if (!active) return false;

    if (active === "all" || active === "new") {
      return filterBuilder.state.filter.groups.length > 0;
    }
    return JSON.stringify(filterBuilder.state.filter) !== JSON.stringify(wire.sync.views.get(active)?.filters);
  };

  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd/Ctrl + Shift + K
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (knowledgeUi.isOpen()) {
          knowledgeUi.close();
        } else {
          knowledgeUi.open();
        }
      }
    };
    const unsubscribeAddItemsToPromptScope = subscribeToGlobalEvent("add-items-to-prompt-scope", (e) => {
      knowledgeUi.close();
    });
    window.addEventListener("keydown", handleKeyDown);
    onCleanup(() => {
      window.removeEventListener("keydown", handleKeyDown);
      unsubscribeAddItemsToPromptScope();
    });
  });

  const filterBuilder = useFilterBuilder<Asset | Concept>();

  createEffect(
    on(
      () => knowledgeUi.activeView(),
      (activeView) => {
        setEditView(false);

        if (!activeView) {
          return;
        }

        if (activeView === "new") {
          return;
        }

        if (activeView === "all") {
          filterBuilder.resetFilter();
          return;
        }
        const view = wire.sync.views.get(activeView);
        if (view) {
          const filters = serverFiltersToUiFilters<Asset | Concept>(wire, view.filters);
          filterBuilder.setState({ filter: filters });
        } else {
          filterBuilder.resetFilter();
          knowledgeUi.setActiveView("all");
        }
      },
    ),
  );

  return (
    <AnimateChildren
      show={knowledgeUi.isOpen()}
      enter={{ opacity: [0, 1], x: [30, 0] }}
      exit={{ opacity: [1, 0], x: [0, 30] }}
      duration={150}
    >
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) {
              knowledgeUi.close();
            }
          }}
        >
          <Dialog.Portal>
            <AnimateChildren
              show={anim.isVisible()}
              enter={{ opacity: [0, 1] }}
              exit={{ opacity: [1, 0] }}
              duration={150}
            >
              {(sAnim) => (
                <Dialog.Overlay
                  ref={sAnim.ref}
                  class="fixed z-50 inset-0 bg-black/30 dark:bg-white/10 animate-fade-in-quick"
                />
              )}
            </AnimateChildren>
            <div ref={anim.ref} class="fixed z-50 inset-0 flex justify-end">
              <Dialog.Content class="py-2 my-2 min-w-[70%] max-h-[calc(100vh-1rem)] mr-2 text-primary-text max-w-xl shadow-2xl bg-panel overflow-y-auto rounded-xl">
                <KnowledgePaneContent
                  selectedConcept={selectedConcept()}
                  setSelectedConcept={setSelectedConcept}
                  hasViewChanges={hasViewChanges()}
                  filterBuilder={filterBuilder}
                  assetsSelection={assetsSelection}
                  conceptsSelection={conceptsSelection}
                  search={search}
                  setSearch={setSearch}
                  filterExpanded={filterExpanded}
                  setFilterExpanded={setFilterExpanded}
                  editView={editView()}
                  setEditView={setEditView}
                />
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
